// src/pages/events/EventForm.jsx
// Handles both creation (/events/new) and editing (/events/:id/edit).
// We branch on whether useParams returns an id.
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Typography, Paper, Stack, TextField, MenuItem, Button,
  IconButton, Chip, Alert, CircularProgress, Divider,
  useMediaQuery, useTheme, InputAdornment, alpha,
} from "@mui/material";
import {
  EventNote, ArrowBack, CloudUpload, Close as CloseIcon,
  AccessTime, LocationOn, Public, People, Tag as TagIcon, Save,
  Visibility, Description as DescriptionIcon, Title as TitleIcon,
} from "@mui/icons-material";
import axios from "axios";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

const API = "https://backend-global-1vq9.onrender.com";

const C = {
  primary: "#0A2E8A", primaryDark: "#061E5C", primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC", primarySofter: "#F2F5FE",
  secondary: "#10682A", secondaryDark: "#0A4A1C", secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  bg: "#FAFBFC", paper: "#FFFFFF",
  border: "#E5E9F0", borderSoft: "#EEF1F6",
  textPrimary: "#0D1117", textSecondary: "#5B6B7E", textMuted: "#8A9BB0",
  warning: "#D97706", warningSoft: "#FEF3C7",
  error: "#DC2626", errorSoft: "#FEE2E2",
};

const PLATFORMS = [
  { value: "zoom",            label: "Zoom",            help: "Paste your Zoom meeting URL" },
  { value: "google-meet",     label: "Google Meet",     help: "Paste your Google Meet link" },
  { value: "youtube-live",    label: "YouTube Live",    help: "Paste your livestream URL — we'll embed it on the page" },
  { value: "microsoft-teams", label: "Microsoft Teams", help: "Paste your Teams meeting link" },
  { value: "in-person",       label: "In Person",       help: "Enter the venue address below" },
  { value: "other",           label: "Other / Custom",  help: "Any other meeting link" },
];

const CATEGORIES = [
  "general", "workshop", "webinar", "ama", "panel", "networking", "career",
];

const VISIBILITIES = [
  { value: "public",        label: "Public", help: "Anyone on the platform can register" },
  { value: "mentees-only",  label: "Mentees only", help: "Only mentees can see and register" },
  { value: "mentors-only",  label: "Mentors only", help: "Only mentors can see and register" },
];

const TIMEZONES = [
  "Africa/Nairobi", "Africa/Dar_es_Salaam", "Africa/Kampala", "Africa/Lagos",
  "Africa/Johannesburg", "Africa/Cairo", "Europe/London", "America/New_York",
  "UTC",
];

const resolveImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("blob:") || path.startsWith("data:")) return path;
  return `${API}/${path.replace(/\\/g, "/").replace(/^\//, "")}`;
};

// Convert a Date object (or ISO string) to the format <input type="datetime-local"> expects: "YYYY-MM-DDTHH:mm"
const toLocalDateTimeInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5, fontSize: 14, bgcolor: C.paper,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.primary },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: 2 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
};

const SectionLabel = ({ icon, children }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25, mt: 0.5 }}>
    {icon}
    <Typography sx={{
      fontSize: 11, fontWeight: 800, letterSpacing: 1.2,
      textTransform: "uppercase", color: C.primary,
    }}>
      {children}
    </Typography>
  </Stack>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function EventForm() {
  const { id } = useParams(); // present when editing
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isEditing = Boolean(id);

  const fileInputRef = useRef(null);

  // ── Form state ────────────────────────────────────────────────
  const [title, setTitle]               = useState("");
  const [description, setDescription]   = useState("");
  const [startTime, setStartTime]       = useState("");
  const [endTime, setEndTime]           = useState("");
  const [timezone, setTimezone]         = useState("Africa/Nairobi");
  const [platform, setPlatform]         = useState("zoom");
  const [meetingUrl, setMeetingUrl]     = useState("");
  const [venueAddress, setVenueAddress] = useState("");
  const [category, setCategory]         = useState("general");
  const [visibility, setVisibility]     = useState("public");
  const [capacity, setCapacity]         = useState("");
  const [tags, setTags]                 = useState([]);
  const [tagInput, setTagInput]         = useState("");
  const [coverFile, setCoverFile]       = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [existingCover, setExistingCover] = useState(""); // for edit mode

  const [submitting, setSubmitting] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [error, setError] = useState("");

  // ── User check — only mentors/superusers reach this page ──
  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); }
    catch { return null; }
  })();
  const userRole = (user?.role || "").toLowerCase();
  const canHost = userRole === "mentor" || userRole === "superuser";

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!canHost) {
      setError("Only mentors and admins can create events.");
    }
  }, [user, canHost, navigate]);

  // ── Load existing event when editing ──
  useEffect(() => {
    if (!isEditing) return;
    const loadEvent = async () => {
      try {
        setLoadingEvent(true);
        const res = await axios.get(`${API}/api/events/${id}`);
        const ev = res.data?.data;
        if (!ev) { setError("Event not found"); return; }

        // Authorization check — only host or superuser can edit
        if (String(ev.hostId) !== String(user?.id) && userRole !== "superuser") {
          setError("You can only edit your own events.");
          return;
        }

        setTitle(ev.title || "");
        setDescription(ev.description || "");
        setStartTime(toLocalDateTimeInput(ev.startTime));
        setEndTime(toLocalDateTimeInput(ev.endTime));
        setTimezone(ev.timezone || "Africa/Nairobi");
        setPlatform(ev.platform || "zoom");
        setMeetingUrl(ev.meetingUrl || "");
        setVenueAddress(ev.venueAddress || "");
        setCategory(ev.category || "general");
        setVisibility(ev.visibility || "public");
        setCapacity(ev.capacity ? String(ev.capacity) : "");
        setTags(ev.tags || []);
        setExistingCover(ev.coverImage || "");
      } catch (err) {
        setError(err.response?.data?.msg || "Failed to load event");
      } finally {
        setLoadingEvent(false);
      }
    };
    loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  // ── Cover-image handling ──
  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Cover must be an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Cover image must be under 5MB");
      return;
    }
    setError("");
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const clearCover = () => {
    setCoverFile(null);
    setCoverPreview("");
    setExistingCover("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Tag input — comma or Enter commits ──
  const commitTag = () => {
    const v = tagInput.trim();
    if (!v) return;
    if (tags.includes(v)) { setTagInput(""); return; }
    setTags((prev) => [...prev, v]);
    setTagInput("");
  };

  const handleTagKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitTag();
    } else if (e.key === "Backspace" && !tagInput && tags.length) {
      setTags((prev) => prev.slice(0, -1));
    }
  };

  // ── Validation ──
  const validate = () => {
    if (!title.trim())       return "Title is required";
    if (!description.trim()) return "Description is required";
    if (!startTime)          return "Start time is required";
    if (!endTime)            return "End time is required";

    const startMs = new Date(startTime).getTime();
    const endMs   = new Date(endTime).getTime();
    if (isNaN(startMs) || isNaN(endMs)) return "Invalid date/time";
    if (endMs <= startMs)               return "End time must be after start time";

    // Allow editing past events but warn on create
    if (!isEditing && startMs < Date.now()) {
      return "Start time must be in the future";
    }

    if (!platform) return "Please choose a platform";

    if (platform === "in-person") {
      if (!venueAddress.trim()) return "Please provide a venue address for in-person events";
    } else {
      if (!meetingUrl.trim()) return "Please provide a meeting URL";
      // Soft URL sanity check (don't be too strict — different platforms have different URL shapes)
      if (!/^https?:\/\//i.test(meetingUrl.trim())) return "Meeting URL must start with http:// or https://";
    }

    const cap = capacity === "" ? 0 : Number(capacity);
    if (capacity !== "" && (isNaN(cap) || cap < 0)) return "Capacity must be a positive number or blank";

    return "";
  };

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      setSubmitting(true);

      // Multipart form — needed for cover image
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      fd.append("startTime", new Date(startTime).toISOString());
      fd.append("endTime", new Date(endTime).toISOString());
      fd.append("timezone", timezone);
      fd.append("platform", platform);
      if (platform === "in-person") {
        fd.append("venueAddress", venueAddress.trim());
        fd.append("meetingUrl", "");
      } else {
        fd.append("meetingUrl", meetingUrl.trim());
        fd.append("venueAddress", "");
      }
      fd.append("category", category);
      fd.append("visibility", visibility);
      fd.append("capacity", capacity || 0);
      tags.forEach((t) => fd.append("tags", t));
      if (coverFile) fd.append("coverImage", coverFile);

      // Host metadata (only used on create)
      if (!isEditing) {
        fd.append("hostId", user.id);
        fd.append("hostName", user.fullName || user.email || "Host");
        fd.append("hostEmail", user.email || "");
        fd.append("hostRole", userRole === "superuser" ? "superuser" : "mentor");
      } else {
        // For edit, send requester info for auth check
        fd.append("requesterId", user.id);
        fd.append("requesterRole", userRole);
      }

      let res;
      if (isEditing) {
        res = await axios.put(`${API}/api/events/${id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axios.post(`${API}/api/events`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      const savedId = res.data?.data?._id || id;
      // Bounce to the detail page
      navigate(`/events/${savedId}`);
    } catch (err) {
      setError(err.response?.data?.msg || err.message || "Failed to save event");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render guard ──
  if (!user) return null;
  if (!canHost) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
        <TopBar />
        <Box flex={1} display="flex" alignItems="center" justifyContent="center" px={3}>
          <Paper elevation={0} sx={{
            p: 4, textAlign: "center", borderRadius: 3, maxWidth: 480,
            border: `1px solid ${C.border}`,
          }}>
            <Typography sx={{ fontWeight: 800, color: C.textPrimary, mb: 1 }}>
              Not authorized
            </Typography>
            <Typography sx={{ color: C.textSecondary, fontSize: 13, mb: 3 }}>
              Only mentors and admins can create or edit events.
            </Typography>
            <Button
              onClick={() => navigate("/events")}
              variant="contained"
              sx={{ bgcolor: C.primary, textTransform: "none", fontWeight: 700, "&:hover": { bgcolor: C.primaryDark } }}
            >
              Back to events
            </Button>
          </Paper>
        </Box>
        <Footer />
      </Box>
    );
  }

  if (loadingEvent) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
        <TopBar />
        <Box flex={1} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress sx={{ color: C.primary }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  const platformHelp = PLATFORMS.find((p) => p.value === platform)?.help || "";
  const visibilityHelp = VISIBILITIES.find((v) => v.value === visibility)?.help || "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, md: 4 }, py: 3, maxWidth: 900, mx: "auto", width: "100%" }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(isEditing ? `/events/${id}` : "/events")}
          sx={{
            color: C.textSecondary, textTransform: "none",
            fontWeight: 700, fontSize: 13, mb: 2,
            "&:hover": { bgcolor: C.primarySofter, color: C.primary },
          }}
        >
          {isEditing ? "Back to event" : "Back to events"}
        </Button>

        {/* Hero header */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: 3, overflow: "hidden",
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            p: { xs: 3, sm: 3.5 },
            mb: 3,
            color: "#fff",
            boxShadow: `0 6px 24px ${C.primary}30`,
          }}
        >
          <Box sx={{
            position: "absolute",
            top: "-50%", right: "-10%",
            width: 220, height: 220, borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} mb={1}>
              <EventNote sx={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }} />
              <Typography sx={{
                fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
                textTransform: "uppercase", color: "rgba(255,255,255,0.75)",
              }}>
                {isEditing ? "Edit Event" : "Create Event"}
              </Typography>
            </Stack>
            <Typography sx={{
              fontWeight: 800, fontSize: { xs: 22, sm: 28 },
              color: "#fff", lineHeight: 1.15, letterSpacing: -0.4,
            }}>
              {isEditing ? "Edit your event" : "Host a new event"}
            </Typography>
            <Typography sx={{
              color: "rgba(255,255,255,0.85)",
              fontSize: { xs: 13, sm: 14 }, maxWidth: 560, mt: 0.5, lineHeight: 1.5,
            }}>
              Workshops, webinars, AMAs, networking — give your event the details people need to show up.
            </Typography>
          </Box>
        </Paper>

        {error && (
          <Alert
            severity="error"
            onClose={() => setError("")}
            sx={{ mb: 2, borderRadius: 2.5, fontSize: 13 }}
          >
            {error}
          </Alert>
        )}

        <Paper
          component="form"
          onSubmit={handleSubmit}
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3.5 }, borderRadius: 3,
            border: `1px solid ${C.border}`, bgcolor: C.paper,
            boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
          }}
        >
          {/* ── Cover image upload ──────────────────────────── */}
          <SectionLabel icon={<CloudUpload sx={{ fontSize: 14, color: C.primary }} />}>
            Cover Image
          </SectionLabel>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleCoverChange}
            style={{ display: "none" }}
          />
          {coverPreview || existingCover ? (
            <Box sx={{
              position: "relative", borderRadius: 3, overflow: "hidden", mb: 3,
              border: `1px solid ${C.border}`,
            }}>
              <Box
                component="img"
                src={coverPreview || resolveImageUrl(existingCover)}
                alt="Event cover preview"
                sx={{ width: "100%", height: { xs: 180, sm: 240 }, objectFit: "cover", display: "block" }}
              />
              <IconButton
                onClick={clearCover}
                sx={{
                  position: "absolute", top: 10, right: 10,
                  bgcolor: "rgba(13,17,23,0.7)", color: "#fff",
                  "&:hover": { bgcolor: "rgba(13,17,23,0.85)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <Button
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  position: "absolute", bottom: 10, right: 10,
                  bgcolor: "rgba(255,255,255,0.95)", color: C.primary,
                  fontWeight: 700, fontSize: 12, textTransform: "none",
                  px: 1.5, py: 0.5, borderRadius: 2,
                  "&:hover": { bgcolor: "#fff" },
                }}
              >
                Replace
              </Button>
            </Box>
          ) : (
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${C.border}`,
                borderRadius: 3,
                p: 4, mb: 3,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                bgcolor: C.primarySofter,
                "&:hover": { borderColor: C.primary, bgcolor: C.primarySoft },
              }}
            >
              <Box sx={{
                width: 56, height: 56, borderRadius: "50%",
                background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                mx: "auto", mb: 1.5,
                boxShadow: `0 6px 14px ${C.primary}30`,
              }}>
                <CloudUpload sx={{ fontSize: 26, color: "#fff" }} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: C.textPrimary, fontSize: 14, mb: 0.5 }}>
                Add a cover image
              </Typography>
              <Typography sx={{ fontSize: 12, color: C.textMuted }}>
                JPG, PNG, or WebP · up to 5MB
              </Typography>
            </Box>
          )}

          {/* ── Basics ──────────────────────────────── */}
          <SectionLabel icon={<TitleIcon sx={{ fontSize: 14, color: C.primary }} />}>
            Event Details
          </SectionLabel>

          <Stack spacing={2} mb={3}>
            <TextField
              label="Title *"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              inputProps={{ maxLength: 200 }}
              sx={fieldSx}
            />
            <TextField
              label="Description *"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth multiline minRows={4} maxRows={10}
              required
              inputProps={{ maxLength: 5000 }}
              helperText={`${description.length} / 5000`}
              sx={fieldSx}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                fullWidth
                sx={fieldSx}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c} sx={{ fontSize: 14, textTransform: "capitalize" }}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKey}
                onBlur={commitTag}
                fullWidth
                placeholder="Press Enter to add"
                helperText="Add comma-separated tags"
                InputProps={{
                  startAdornment: tags.length > 0 ? (
                    <InputAdornment position="start" sx={{ flexWrap: "wrap", gap: 0.5, maxWidth: "70%" }}>
                      {tags.map((t, i) => (
                        <Chip
                          key={i}
                          label={t}
                          size="small"
                          onDelete={() => setTags((prev) => prev.filter((_, idx) => idx !== i))}
                          sx={{ bgcolor: C.primarySoft, color: C.primary, fontWeight: 600, fontSize: 11, height: 22 }}
                        />
                      ))}
                    </InputAdornment>
                  ) : null,
                }}
                sx={fieldSx}
              />
            </Stack>
          </Stack>

          <Divider sx={{ borderColor: C.borderSoft, mb: 3 }} />

          {/* ── Date & time ─────────────────────────── */}
          <SectionLabel icon={<AccessTime sx={{ fontSize: 14, color: C.primary }} />}>
            When
          </SectionLabel>

          <Stack spacing={2} mb={3}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Start Time *"
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                fullWidth required
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
              <TextField
                label="End Time *"
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                fullWidth required
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
            </Stack>
            <TextField
              select
              label="Timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              fullWidth
              sx={fieldSx}
            >
              {TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz} sx={{ fontSize: 14 }}>{tz}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <Divider sx={{ borderColor: C.borderSoft, mb: 3 }} />

          {/* ── Platform & meeting link ─────────────── */}
          <SectionLabel icon={<LocationOn sx={{ fontSize: 14, color: C.primary }} />}>
            Where
          </SectionLabel>

          <Stack spacing={2} mb={3}>
            <TextField
              select
              label="Platform *"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              fullWidth required
              helperText={platformHelp}
              sx={fieldSx}
            >
              {PLATFORMS.map((p) => (
                <MenuItem key={p.value} value={p.value} sx={{ fontSize: 14 }}>{p.label}</MenuItem>
              ))}
            </TextField>

            {platform === "in-person" ? (
              <TextField
                label="Venue Address *"
                value={venueAddress}
                onChange={(e) => setVenueAddress(e.target.value)}
                fullWidth required multiline minRows={2}
                placeholder="Building, street, city"
                sx={fieldSx}
              />
            ) : (
              <TextField
                label="Meeting URL *"
                value={meetingUrl}
                onChange={(e) => setMeetingUrl(e.target.value)}
                fullWidth required
                placeholder="https://..."
                sx={fieldSx}
              />
            )}
          </Stack>

          <Divider sx={{ borderColor: C.borderSoft, mb: 3 }} />

          {/* ── Audience & capacity ─────────────────── */}
          <SectionLabel icon={<Visibility sx={{ fontSize: 14, color: C.primary }} />}>
            Audience
          </SectionLabel>

          <Stack spacing={2} mb={4}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                label="Visibility"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                fullWidth
                helperText={visibilityHelp}
                sx={fieldSx}
              >
                {VISIBILITIES.map((v) => (
                  <MenuItem key={v.value} value={v.value} sx={{ fontSize: 14 }}>{v.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Capacity"
                type="number"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                fullWidth
                helperText="Leave blank for unlimited"
                inputProps={{ min: 0 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <People sx={{ fontSize: 18, color: C.textMuted }} />
                    </InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />
            </Stack>
          </Stack>

          {/* ── Submit buttons ──────────────────────── */}
          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={1.5}
            justifyContent="flex-end"
          >
            <Button
              type="button"
              onClick={() => navigate(isEditing ? `/events/${id}` : "/events")}
              disabled={submitting}
              sx={{
                textTransform: "none", fontWeight: 700, fontSize: 14,
                color: C.textSecondary, px: 3, py: 1.25, borderRadius: 2.5,
                border: `1px solid ${C.border}`,
                "&:hover": { bgcolor: C.borderSoft },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              startIcon={
                submitting
                  ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                  : <Save />
              }
              sx={{
                textTransform: "none", fontWeight: 800, fontSize: 14,
                background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
                color: "#fff", px: 3, py: 1.25, borderRadius: 2.5,
                boxShadow: `0 4px 14px ${C.primary}30`,
                "&:hover": { boxShadow: `0 6px 18px ${C.primary}45` },
                "&.Mui-disabled": { background: "#CCC", color: "#fff" },
              }}
            >
              {submitting
                ? (isEditing ? "Saving..." : "Publishing...")
                : (isEditing ? "Save Changes" : "Publish Event")}
            </Button>
          </Stack>
        </Paper>
      </Box>

      <Footer />
    </Box>
  );
}